import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { CButton } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { freeSet } from '@coreui/icons';

import apiController from '../../controllers/Api';

import './style.scss';

class DocumentAgent extends Component {
  state = {
    open: false,
    loading: false,
    uploadingFile: false,
    isDragOver: false,
    input: '',
    selectedFile: null,
    pendingPurchaseOrderDraft: null,
    messages: [
      {
        role: 'assistant',
        text: 'Envia uma proposta ou ordem em PDF/imagem e diz-me o projeto. Eu preparo a ordem de compra para validação.',
        actions: [],
      },
    ],
  };

  componentDidMount() {
    if (this.props.accessToken) {
      apiController.setProps(this.props);
      apiController.createInstance();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.accessToken && this.props.accessToken !== prevProps.accessToken) {
      apiController.setProps(this.props);
      apiController.createInstance();
    }
  }

  appendMessage(message) {
    this.setState({
      messages: this.state.messages.concat(message),
    });
  }

  setLastAssistant(response) {
    const allowedActions = ['create_purchase_order_from_draft'];
    const actions = response && response.actions ? response.actions.filter((action) => allowedActions.includes(action.type)) : [];

    this.appendMessage({
      role: 'assistant',
      text: response && response.message ? response.message : 'Não consegui obter resposta da API.',
      actions: actions,
      data: response && response.data ? response.data : null,
      draft: response && response.draft ? response.draft : null,
    });
  }

  isDraftReady(response) {
    const draft = response && response.draft ? response.draft : null;
    const lines = draft && Array.isArray(draft.lines) ? draft.lines : [];

    return !!(
      response &&
      response.state === 'draft-ready' &&
      draft &&
      draft.project &&
      draft.project.id &&
      lines.length
    );
  }

  dispatchDataChanged(path, payload = {}) {
    if (typeof window === 'undefined' || !window.dispatchEvent) return;

    window.dispatchEvent(new CustomEvent('prodtrack:data-changed', {
      detail: {
        path,
        ...payload,
      },
    }));
  }

  buildAssistantErrorMessage(error, fallback) {
    const response = error && error.response ? error.response : error;
    const status = response && response.status ? response.status : null;
    const data = response && response.data ? response.data : {};
    const apiMessage = (
      data && data.error && data.error.message ? data.error.message :
        data && data.message ? data.message :
          error && error.message ? error.message :
            ''
    );
    const normalized = String(apiMessage || '').toLowerCase();
    let reason = 'a API não devolveu uma resposta utilizável para este passo.';
    let nextStep = 'tenta novamente. Se voltar a acontecer, eu preciso de ver a configuração da API ou o ficheiro original.';

    if (status === 401) {
      reason = 'a sessão não está autenticada ou expirou.';
      nextStep = 'faz refresh/login e tenta outra vez.';
    } else if (status === 403) {
      reason = 'a tua conta não tem permissão para executar esta ação.';
      nextStep = 'temos de ajustar permissões para o assistente poder ler/criar este tipo de dados.';
    } else if (status === 404) {
      reason = 'não encontrei o ficheiro ou endpoint necessário na API.';
      nextStep = 'volta a anexar o ficheiro; se persistir, a API pode não estar atualizada.';
    } else if (normalized.includes('missing openai_api_key') || normalized.includes('missing node_openai_api_key')) {
      reason = 'o serviço de IA não tem a chave OpenAI configurada no ambiente da API.';
      nextStep = 'o ficheiro ficou anexado, mas só consigo ler propostas automaticamente depois dessa configuração estar ativa.';
    } else if (normalized.includes('timed out') || normalized.includes('timeout')) {
      reason = 'a leitura do ficheiro demorou demasiado tempo e foi interrompida.';
      nextStep = 'tenta novamente ou envia uma versão mais leve/legível do PDF.';
    } else if (normalized.includes('unsupported file type')) {
      reason = 'o formato do ficheiro não é suportado neste fluxo.';
      nextStep = 'usa PDF, PNG ou JPG.';
    } else if (normalized.includes('invalid file name')) {
      reason = 'o nome do ficheiro falhou a validação de segurança.';
      nextStep = 'renomeia o ficheiro para um nome simples e arrasta-o novamente.';
    } else if (status >= 500) {
      reason = 'a API teve um erro interno enquanto tentava processar o pedido.';
      nextStep = 'vou precisar de validar os logs/configuração do backend se isto se repetir.';
    } else if (apiMessage) {
      reason = apiMessage;
    }

    return [
      fallback,
      `Motivo: ${reason}`,
      `Próximo passo: ${nextStep}`,
    ].join('\n');
  }

  getActionLabel(action) {
    if (!action) return 'Executar';
    if (action.type === 'create_purchase_order_from_draft') return 'Criar ordem de compra';
    return action.label || 'Executar';
  }

  isConfirmationIntent(text) {
    const normalized = String(text || '').trim().toLowerCase();
    return [
      'sim',
      'ok',
      'confirmo',
      'podes criar',
      'cria',
      'criar',
      'avanca',
      'avança',
      'looks good',
    ].some((term) => normalized === term || normalized.includes(term));
  }

  async sendMessage(message) {
    const text = String(message || this.state.input || '').trim();
    if (!text || this.state.loading) return;
    const selectedFile = this.state.selectedFile;
    const pendingPurchaseOrderDraft = this.state.pendingPurchaseOrderDraft;

    this.appendMessage({
      role: 'user',
      text: text,
      actions: [],
    });

    this.setState({
      input: '',
      loading: true,
      open: true,
    });

    try {
      if (pendingPurchaseOrderDraft && this.isConfirmationIntent(text)) {
        await this.createPurchaseOrderFromDraft(pendingPurchaseOrderDraft);
      } else if (selectedFile) {
        await this.preparePurchaseOrderDraft(text, selectedFile);
      } else {
        this.appendMessage({
          role: 'assistant',
          text: 'Para já consigo preparar ordens de compra a partir de uma proposta ou ordem em PDF/imagem. Arrasta o ficheiro para aqui e diz-me o projeto.',
          actions: [],
        });
      }
    } catch (error) {
      this.appendMessage({
        role: 'assistant',
        text: this.buildAssistantErrorMessage(error, 'O pedido ficou incompleto antes de eu conseguir terminar.'),
        actions: [],
      });
    } finally {
      this.setState({
        loading: false,
      });
    }
  }

  isSupportedAgentFile(file) {
    if (!file) return false;

    const supportedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const supportedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];
    const name = String(file.name || '').toLowerCase();

    return supportedTypes.includes(file.type) || supportedExtensions.some((extension) => name.endsWith(extension));
  }

  async uploadFileObject(file) {
    if (!file || this.state.uploadingFile) return;

    if (!this.isSupportedAgentFile(file)) {
      this.setState({
        open: true,
        isDragOver: false,
      });
      this.appendMessage({
        role: 'assistant',
        text: 'Só consigo anexar PDF, PNG ou JPG para preparar ordens de compra.',
        actions: [],
      });
      return;
    }

    const form = new FormData();
    form.append('file', file);
    form.append('source', 'document-agent');
    form.append('purchaseOrderReviewState', 'uploaded');

    this.setState({
      uploadingFile: true,
      open: true,
      isDragOver: false,
    });

    let error = null;
    let resp = null;

    try {
      const result = await apiController.upload('document-agent', '/files', form);
      error = result.error;
      resp = result.resp;
    } catch (uploadError) {
      error = uploadError;
    } finally {
      this.setState({
        uploadingFile: false,
      });
    }

    const uploadedFile = resp && resp.files && resp.files[0] ? resp.files[0] : null;

    if (error || !uploadedFile) {
      this.appendMessage({
        role: 'assistant',
        text: this.buildAssistantErrorMessage(error, 'Não consegui anexar o ficheiro.'),
        actions: [],
      });
    } else {
      this.setState({
        selectedFile: uploadedFile,
        pendingPurchaseOrderDraft: null,
      });
      this.appendMessage({
        role: 'assistant',
        text: `Ficheiro anexado: ${uploadedFile.originalname || uploadedFile.name}. Diz-me a que projeto pertence e pede-me para preparar a ordem de compra.`,
        actions: [],
      });
    }
  }

  handleDragEnter(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.state.uploadingFile || this.state.loading) return;

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    this.setState({
      open: true,
      isDragOver: true,
    });
  }

  handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    if (!this.state.isDragOver && !this.state.uploadingFile && !this.state.loading) {
      this.setState({
        isDragOver: true,
      });
    }
  }

  handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();

    if (event.currentTarget && event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;

    this.setState({
      isDragOver: false,
    });
  }

  async handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.state.uploadingFile || this.state.loading) return;

    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];

    this.setState({
      open: true,
      isDragOver: false,
    });

    await this.uploadFileObject(file);
  }

  async preparePurchaseOrderDraft(text, selectedFile) {
    const {error, resp} = await apiController.query(
      'document-agent',
      'POST',
      '/invoices/review-purchase-order-file-with-ai',
      {},
      {
        file: selectedFile.id,
        message: text,
      },
    );

    if (error) {
      this.appendMessage({
        role: 'assistant',
        text: this.buildAssistantErrorMessage(error, 'Ainda não consegui preparar a ordem de compra a partir deste ficheiro.'),
        actions: [],
      });
      return;
    }

    this.setState({
      pendingPurchaseOrderDraft: resp && resp.draft ? resp.draft : null,
    });
    this.setLastAssistant(resp);

    if (this.isDraftReady(resp)) {
      await this.createPurchaseOrderFromDraft(resp.draft, {automatic: true});
    } else if (resp && ['not-purchase-order-source', 'needs-lines'].includes(resp.state)) {
      this.setState({
        selectedFile: null,
      });
    }
  }

  async createPurchaseOrderFromDraft(draft, options = {}) {
    if (!draft) return false;

    const {error, resp} = await apiController.query(
      'document-agent',
      'POST',
      '/invoices/create-purchase-order-from-draft',
      {},
      {
        file: draft.file,
        draft: draft,
        confirm: true,
      },
    );

    if (error) {
      this.appendMessage({
        role: 'assistant',
        text: this.buildAssistantErrorMessage(error, 'Não consegui criar a ordem de compra.'),
        actions: [],
      });
      return false;
    }

    this.setState({
      pendingPurchaseOrderDraft: null,
      selectedFile: null,
    });

    if (resp && resp.order) {
      this.dispatchDataChanged('/invoices', {
        resource: 'Invoice',
        reason: 'purchase-order-created',
        id: resp.order.id,
      });
    }

    this.setLastAssistant({
      ...resp,
      draft: options.automatic ? null : resp.draft,
      actions: [],
    });

    return true;
  }

  async runAction(action) {
    if (!action) return;

    if (action.type === 'create_purchase_order_from_draft') {
      this.setState({
        loading: true,
      });
      try {
        await this.createPurchaseOrderFromDraft(action.payload && action.payload.draft);
      } catch (error) {
        this.appendMessage({
          role: 'assistant',
          text: this.buildAssistantErrorMessage(error, 'Não consegui criar a ordem de compra.'),
          actions: [],
        });
      } finally {
        this.setState({
          loading: false,
        });
      }
      return;
    }
  }

  renderPurchaseOrderDraft(message) {
    const draft = message && message.draft;
    if (!draft) return null;

    return (
      <div className="document-agent-po-draft">
        <div className="document-agent-po-grid">
          <div>
            <span>Supplier</span>
            <strong>{draft.supplierName || draft.contact?.name || 'Not resolved'}</strong>
          </div>
          <div>
            <span>Project</span>
            <strong>
              {draft.project ? `${draft.project.internalNumber || ''}${draft.project.internalNumber ? ' | ' : ''}${draft.project.name || ''}` : 'Needs project'}
            </strong>
          </div>
          <div>
            <span>Total</span>
            <strong>{Number(draft.total || 0).toFixed(2)} {draft.currency || 'EUR'}</strong>
          </div>
          <div>
            <span>Lines</span>
            <strong>{(draft.lines || []).length}</strong>
          </div>
        </div>
        {
          draft.lines && draft.lines.length ? (
            <div className="document-agent-po-lines">
              {
                draft.lines.slice(0, 4).map((line, index) => (
                  <div key={index}>
                    <span>{line.description || 'Line item'}</span>
                    <small>{Number(line.quantity || 0)} {line.unit || 'un'} · {Number(line.total || 0).toFixed(2)} {draft.currency || 'EUR'}</small>
                  </div>
                ))
              }
              {
                draft.lines.length > 4 ? (
                  <small>+{draft.lines.length - 4} more lines</small>
                ) : null
              }
            </div>
          ) : null
        }
      </div>
    );
  }

  renderMessages() {
    return this.state.messages.slice(-8).map((message, index) => {
      return (
        <div key={index} className={'document-agent-message document-agent-message-' + message.role}>
          <div className="document-agent-bubble">
            {message.text}
          </div>
          {this.renderPurchaseOrderDraft(message)}
          {
            message.actions && message.actions.length ? (
              <div className="document-agent-actions">
                {
                  message.actions.map((action, actionIndex) => (
                    <CButton
                      key={actionIndex}
                      size="sm"
                      color="light"
                      onClick={() => this.runAction(action)}
                    >
                      {this.getActionLabel(action)}
                    </CButton>
                  ))
                }
              </div>
            ) : null
          }
        </div>
      );
    });
  }

  renderPanel() {
    if (!this.state.open) return null;

    const selectedFileName = this.state.selectedFile ? (this.state.selectedFile.originalname || this.state.selectedFile.name) : '';

    return (
      <div
        className={'document-agent-panel' + (this.state.isDragOver ? ' is-drag-over' : '')}
        onDragEnter={(event) => this.handleDragEnter(event)}
        onDragOver={(event) => this.handleDragOver(event)}
        onDragLeave={(event) => this.handleDragLeave(event)}
        onDrop={(event) => this.handleDrop(event)}
      >
        <div className="document-agent-header">
          <div>
            <div className="document-agent-title">Assistente documental</div>
            <div className="document-agent-subtitle">Ordens de compra</div>
          </div>
          <CButton
            size="sm"
            color="link"
            className="document-agent-close"
            onClick={() => this.setState({open: false})}
            title="Fechar"
          >
            <CIcon content={freeSet.cilX} />
          </CButton>
        </div>

        <div className="document-agent-body">
          {this.renderMessages()}
          {
            this.state.loading ? (
              <div className="document-agent-loading">A preparar resposta...</div>
            ) : null
          }
        </div>

        <div className="document-agent-composer">
          <div
            className={
              'document-agent-dropzone' +
              (this.state.isDragOver ? ' is-active' : '') +
              (this.state.selectedFile ? ' has-file' : '')
            }
            aria-label="Zona para arrastar proposta ou ordem"
            title={selectedFileName || 'Arrasta aqui a proposta ou ordem'}
            onDragEnter={(event) => this.handleDragEnter(event)}
            onDragOver={(event) => this.handleDragOver(event)}
            onDrop={(event) => this.handleDrop(event)}
          >
            <strong>
              {this.state.uploadingFile ? 'A anexar...' : (this.state.selectedFile ? 'Ficheiro anexado' : 'Arrasta aqui a proposta ou ordem')}
            </strong>
            <span>
              {selectedFileName || 'PDF, PNG ou JPG'}
            </span>
          </div>
          <textarea
            className="form-control"
            value={this.state.input}
            rows={2}
            placeholder="Escreve o pedido..."
            onChange={(event) => this.setState({input: event.target.value})}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                this.sendMessage();
              }
            }}
          />
          <CButton
            color="primary"
            disabled={this.state.loading || !this.state.input.trim()}
            onClick={() => this.sendMessage()}
          >
            Enviar
          </CButton>
        </div>
      </div>
    );
  }

  render() {
    if (!this.props.accessToken) return null;

    return (
      <div className="document-agent">
        {this.renderPanel()}
        <CButton
          color="primary"
          className="document-agent-launcher"
          onClick={() => {
            if (!this.state.open) this.setState({open: true});
            else this.setState({open: false});
          }}
          title="Assistente documental"
        >
          <CIcon content={freeSet.cilInfo} />
        </CButton>
      </div>
    );
  }
}

export default withRouter(DocumentAgent);
